/**
 * DFY Producer Vault fixture data — categories and packs (prompts, presets,
 * structures, hooks, metadata templates, marketing assets).
 *
 * MOCK/FIXTURE DATA, moved verbatim from the monolith. Stands in for what the
 * original comment called "packs.json / prompts.json / etc." A backend would
 * serve these from a vault_packs table; see docs/api-contract.md, "Vault".
 */
import type { VaultCategory, VaultPack } from '../types/domain';
import { DFY_PRODUCER_VAULT_SEEDS } from './dfyProducerVaultSeeds';

export const VAULT_CATEGORIES: VaultCategory[] = [
  { id: 'prompts', name: 'AI Music Prompts', icon: 'FileText' },
  { id: 'structures', name: 'Song Structures', icon: 'Layers' },
  { id: 'presets', name: 'Genre Presets', icon: 'Sliders' },
  { id: 'hooks', name: 'Hook Library', icon: 'Zap' },
  { id: 'metadata', name: 'Metadata Templates', icon: 'FileMusic' },
  { id: 'marketing', name: 'Marketing Assets', icon: 'Megaphone' },
  { id: 'trending', name: 'Trending Packs', icon: 'TrendingUp' },
  { id: 'seasonal', name: 'Seasonal Packs', icon: 'Star' },
];

export const VAULT_PACKS: VaultPack[] = [
  {
    id: 'afrobeats-collection',
    name: 'Afrobeats Producer Collection',
    category: 'Trending Packs',
    description:
      'A complete toolkit for modern Afrobeats and Amapiano production — prompts, presets, structures, hooks, and release-ready marketing copy.',
    author: 'Lyrixsa Team',
    version: '2.1',
    releaseDate: 'Jun 12, 2026',
    updated: 'Jul 22, 2026',
    prompts: [
      {
        id: 'ap1',
        title: 'Sunset Log Drum Groove',
        genre: 'Afrobeats',
        mood: 'Uplifting',
        difficulty: 'Beginner',
        text: 'An uplifting Afrobeats track with warm log drums, rhythmic percussion, melodic synth chords, and joyful call-and-response vocals, around 108 BPM.',
      },
      {
        id: 'ap2',
        title: 'Amapiano Late Night',
        genre: 'Amapiano',
        mood: 'Smooth',
        difficulty: 'Intermediate',
        text: 'A smooth Amapiano groove with deep bass, soft piano stabs, shaker percussion, and a laid-back late-night atmosphere, around 112 BPM.',
      },
      {
        id: 'ap3',
        title: 'Festival Afro-House Anthem',
        genre: 'Afrobeats',
        mood: 'Energetic',
        difficulty: 'Advanced',
        text: 'A high-energy Afro-house anthem built for festival crowds — driving log drums, layered percussion, big synth stabs, and a euphoric drop, around 118 BPM.',
      },
    ],
    presets: [
      {
        id: 'aps1',
        name: 'Warm Log Drum',
        tempo: 'Medium',
        mood: 'Uplifting',
        energy: 70,
        vocals: 'Male vocal',
        structure: 'Intro–Verse–Chorus–Verse–Chorus–Outro',
      },
      {
        id: 'aps2',
        name: 'Amapiano Groove',
        tempo: 'Medium',
        mood: 'Smooth',
        energy: 55,
        vocals: 'Female vocal',
        structure: 'Intro–Groove–Verse–Groove–Verse–Outro',
      },
    ],
    structures: [
      {
        id: 'ast1',
        name: 'Afrobeats Radio Structure',
        layout: 'Intro – Verse – Chorus – Verse – Chorus – Bridge – Chorus – Outro',
      },
      {
        id: 'ast2',
        name: 'Amapiano Extended Groove',
        layout: 'Intro – Build – Groove – Break – Groove – Outro',
      },
    ],
    hooks: [
      {
        id: 'ah1',
        text: 'Turn the lights down, let the log drums lead the way',
        genre: 'Afrobeats',
      },
      { id: 'ah2', text: 'Every night feels golden when the piano hits', genre: 'Amapiano' },
    ],
    metadata: [
      {
        id: 'am1',
        platform: 'Spotify',
        title: 'Afrobeats release metadata',
        text: 'Genre: Afrobeats / Amapiano · Mood: Uplifting, Danceable · Suggested playlists: African Heat, Afrobeats Hits',
      },
      {
        id: 'am2',
        platform: 'TikTok',
        title: 'Short-form hook caption',
        text: '"This log drum groove is UNDEFEATED 🔥🥁 #Afrobeats #Amapiano #NewMusic"',
      },
    ],
    marketing: [
      {
        id: 'amk1',
        platform: 'Instagram Caption',
        title: 'Launch caption',
        text: 'New heat just dropped 🔥 Stream the full track now — link in bio. #Afrobeats #NewRelease',
      },
      {
        id: 'amk2',
        platform: 'YouTube Description',
        title: 'Video description',
        text: 'Official audio for the new single. Produced with Lyrixsa AI. Stream on all platforms — links below.',
      },
    ],
  },
  {
    id: 'synthwave-essentials',
    name: 'Synthwave Essentials',
    category: 'Genre Presets',
    description:
      'Neon-soaked prompts, presets, and hooks for retro-futuristic synthwave and outrun productions.',
    author: 'Lyrixsa Team',
    version: '1.4',
    releaseDate: 'May 3, 2026',
    updated: 'Jun 30, 2026',
    prompts: [
      {
        id: 'sp1',
        title: 'Midnight Drive',
        genre: 'Synthwave',
        mood: 'Nostalgic',
        difficulty: 'Beginner',
        text: 'A nostalgic synthwave track with driving arpeggios, gated reverb drums, and a warm analog bassline, evoking a midnight drive through neon city lights, 116 BPM.',
      },
      {
        id: 'sp2',
        title: 'Outrun Chase',
        genre: 'Synthwave',
        mood: 'Intense',
        difficulty: 'Advanced',
        text: 'A high-tension outrun synthwave track with pulsing bass, aggressive arps, and cinematic tension building toward a big chorus, 132 BPM.',
      },
    ],
    presets: [
      {
        id: 'sps1',
        name: 'Neon Arps',
        tempo: 'Fast',
        mood: 'Energetic',
        energy: 82,
        vocals: 'Instrumental only',
        structure: 'Intro–Build–Drop–Break–Drop–Outro',
      },
    ],
    structures: [
      { id: 'sst1', name: 'Outrun Build', layout: 'Intro – Build – Drop – Break – Drop – Outro' },
    ],
    hooks: [{ id: 'sh1', text: 'Chasing taillights down a neon highway', genre: 'Synthwave' }],
    metadata: [
      {
        id: 'sm1',
        platform: 'Apple Music',
        title: 'Synthwave release metadata',
        text: 'Genre: Synthwave · Mood: Nostalgic, Cinematic · Suggested playlists: Night Drive, Outrun Radio',
      },
    ],
    marketing: [
      {
        id: 'smk1',
        platform: 'Facebook Post',
        title: 'Launch post',
        text: 'Step into the neon. Our new synthwave single is out now — perfect for your late-night drive playlist.',
      },
    ],
  },
  {
    id: 'lofi-study-pack',
    name: 'Lo-Fi Study Pack',
    category: 'AI Music Prompts',
    description:
      'Calm, warm lo-fi instrumentals built for focus, study, and ambient background listening.',
    author: 'Lyrixsa Team',
    version: '1.2',
    releaseDate: 'Apr 18, 2026',
    updated: 'May 20, 2026',
    prompts: [
      {
        id: 'lp1',
        title: 'Rainy Window Study',
        genre: 'Lo-fi instrumental',
        mood: 'Calm',
        difficulty: 'Beginner',
        text: 'A calm lo-fi instrumental with warm piano, soft vinyl crackle, mellow bass, and gentle rain ambience, ideal for studying, 78 BPM.',
      },
      {
        id: 'lp2',
        title: 'Golden Hour Coffee Shop',
        genre: 'Lo-fi instrumental',
        mood: 'Dreamy',
        difficulty: 'Beginner',
        text: 'A dreamy lo-fi track with jazzy guitar chords, soft drums, and a warm coffee-shop atmosphere, 82 BPM.',
      },
    ],
    presets: [
      {
        id: 'lps1',
        name: 'Warm Vinyl',
        tempo: 'Slow',
        mood: 'Calm',
        energy: 25,
        vocals: 'Instrumental only',
        structure: 'Loop-based, minimal structure',
      },
    ],
    structures: [
      { id: 'lst1', name: 'Ambient Loop', layout: 'Intro – Loop A – Loop B – Loop A – Outro' },
    ],
    hooks: [],
    metadata: [
      {
        id: 'lm1',
        platform: 'YouTube Music',
        title: 'Lo-fi release metadata',
        text: 'Genre: Lo-fi / Chillhop · Mood: Calm, Focused · Suggested playlists: Study Beats, Chill Lo-fi',
      },
    ],
    marketing: [
      {
        id: 'lmk1',
        platform: 'Instagram Caption',
        title: 'Launch caption',
        text: 'Your new study soundtrack is here 📚☕ Link in bio.',
      },
    ],
  },
  {
    id: 'cinematic-trailer-toolkit',
    name: 'Cinematic Trailer Toolkit',
    category: 'Song Structures',
    description:
      'Epic, tension-building prompts and structures designed for trailers, openers, and dramatic scenes.',
    author: 'Lyrixsa Team',
    version: '1.0',
    releaseDate: 'Jul 1, 2026',
    updated: 'Jul 1, 2026',
    prompts: [
      {
        id: 'cp1',
        title: 'Rising Tension Trailer',
        genre: 'Cinematic',
        mood: 'Epic',
        difficulty: 'Advanced',
        text: 'An epic cinematic trailer track with rising strings, deep braams, powerful percussion, and a massive climactic drop, 90 BPM.',
      },
    ],
    presets: [
      {
        id: 'cps1',
        name: 'Epic Braam Build',
        tempo: 'Medium',
        mood: 'Epic',
        energy: 90,
        vocals: 'Instrumental only',
        structure: 'Intro – Rise – Impact – Aftermath',
      },
    ],
    structures: [{ id: 'cst1', name: 'Trailer Arc', layout: 'Intro – Rise – Impact – Aftermath' }],
    hooks: [{ id: 'ch1', text: 'The moment everything changes', genre: 'Cinematic' }],
    metadata: [
      {
        id: 'cm1',
        platform: 'Spotify',
        title: 'Cinematic release metadata',
        text: 'Genre: Cinematic / Trailer · Mood: Epic, Dramatic · Suggested playlists: Epic Trailer Music, Cinematic Scores',
      },
    ],
    marketing: [
      {
        id: 'cmk1',
        platform: 'YouTube Description',
        title: 'Trailer music description',
        text: 'Epic cinematic trailer track, perfect for film, game, and video promos. Licensed for commercial use.',
      },
    ],
  },
  ...DFY_PRODUCER_VAULT_SEEDS,
];
